// Ответы разбора документа: выжимка карточкой по разделам, спорные места с пояснениями, ответы на вопросы.
// У каждого пункта — дословная цитата из файла; в выжимке она раскрывается по нажатию, чтобы карточка читалась целиком.
import { Fragment } from "react";
import { openSource } from "../components/Dialogs";
import type { SourceRef } from "../store";
import { PlenumAct, plenumChecked } from "./plenum";

export type ReviewPoint = {
  kind: "fact" | "explanation" | "suggestion"; heading: string; text: string; sources: SourceRef[];
  section?: string; details?: { label: string; text: string }[];
};
// law — действующий текст статьи из базы законов Правосвода (pravosvod/laws.py, norm_text) или пункта постановления
// Пленума ВС (kind = "plenum", pravosvod/plenum.py, norm_text): id — пункт для «Весь текст постановления», url — КонсультантПлюс.
export type NormLaw = {
  label: string; checked: string; status: "ok" | "repealed" | "no_article" | "not_loaded"; text: string; full: string;
  kind?: "plenum"; id?: string; url?: string;
};
export type NormGroup = { title: string; kind: string; refs: { ref: string; sources: SourceRef[]; law?: NormLaw }[] };

const NORM_STATUS: Record<NormLaw["status"], string> = {
  ok: "действует", repealed: "утратила силу", no_article: "такой статьи в кодексе нет", not_loaded: "закона нет в базе Правосвода",
};
const PLENUM_STATUS: Record<NormLaw["status"], string> = {
  ok: "в действующей редакции", repealed: "пункт исключён", no_article: "такого пункта в постановлении нет",
  not_loaded: "постановления нет в базе Правосвода",
};
const statusOf = (law: NormLaw) => (law.kind === "plenum" ? PLENUM_STATUS : NORM_STATUS)[law.status];
const checkedOf = (law: NormLaw) =>
  !law.checked ? "" : law.kind === "plenum" ? `, ${plenumChecked(law.checked)} ${law.checked}` : `, сверено ${law.checked}`;
export type ReviewAnswer = {
  points: ReviewPoint[]; clarification?: string; note?: string; mode?: string;
  profile?: string; sections?: [string, string][]; norms?: NormGroup[];
};

const LABELS = { fact: "По тексту", explanation: "Вывод ИИ", suggestion: "Предлагаемая редакция" };

function Citation({ docId, source }: { docId: string; source: SourceRef }) {
  return (
    <div className="review-citation">
      <blockquote>{source.quote}</blockquote>
      <button type="button" className="link-button" onClick={() => openSource(docId, source)}>{`${source.label} · Открыть цитату ↗`}</button>
    </div>
  );
}

// Цитаты под пунктом выжимки: свёрнуты, по нажатию — дословный текст и переход к месту в документе.
function FoldedQuotes({ docId, sources }: { docId: string; sources: SourceRef[] }) {
  if (!sources.length) return null;
  const pages = [...new Set(sources.map((source) => source.label))].join(", ");
  return (
    <details className="review-quotes">
      <summary>{sources.length > 1 ? `Цитаты (${sources.length}) · ${pages}` : `Цитата · ${pages}`}</summary>
      {sources.map((source, i) => <Citation key={i} docId={docId} source={source} />)}
    </details>
  );
}

// Пункт выжимки: суть своими словами; у договора — ещё «Что это значит для сторон».
function SummaryItem({ point, docId }: { point: ReviewPoint; docId: string }) {
  return (
    <div className="review-item">
      <p className="review-item-text">
        {point.heading ? <strong>{point.heading}. </strong> : null}{point.text}
        {point.kind === "explanation" ? <span className="review-tag">Вывод ИИ</span> : null}
      </p>
      {(point.details || []).map((detail, i) => (
        <p key={i} className="review-item-practice"><span>{detail.label}</span>{detail.text}</p>
      ))}
      <FoldedQuotes docId={docId} sources={point.sources} />
    </div>
  );
}

// Действующий текст нормы: нужная часть, по нажатию — вся статья; отметка, если утратила силу или её нет. У пункта
// Пленума — весь текст постановления по раскрытии и ссылка на КонсультантПлюс.
function NormText({ law }: { law: NormLaw }) {
  return (
    <details className={`norm-text is-${law.status}`}>
      <summary><strong>{law.label}</strong> — {statusOf(law)}{checkedOf(law)}</summary>
      {law.text ? <blockquote>{law.text}</blockquote> : null}
      {law.full ? (
        <details className="norm-full"><summary>Весь текст статьи</summary><blockquote>{law.full}</blockquote></details>
      ) : null}
      {law.kind === "plenum" && law.id ? <PlenumAct id={law.id} /> : null}
      {law.url ? <p className="norm-source"><a href={law.url} target="_blank" rel="noreferrer">Текст на КонсультантПлюс</a></p> : null}
    </details>
  );
}

// Нормы и практика, на которые ссылается документ: список строит код по тексту, а не нейросеть.
function Norms({ norms, docId }: { norms: NormGroup[]; docId: string }) {
  const found = norms.flatMap((group) => group.refs.filter((ref) => ref.law && ref.law.status !== "not_loaded").map((ref) => ref.law!));
  const texts = found.filter((law) => law.kind !== "plenum");
  const explanations = found.filter((law) => law.kind === "plenum");
  return (
    <section className="review-section review-norms">
      <h3>Нормы и практика в тексте</h3>
      <dl>
        {norms.map((group) => (
          <Fragment key={group.title}>
            <dt>{group.title}</dt>
            <dd>
              {group.refs.map((ref) => (
                <button key={ref.ref || "mention"} type="button" className={`norm-chip${ref.law ? ` is-${ref.law.status}` : ""}`}
                  title={ref.law ? `${statusOf(ref.law)} · ${ref.sources[0].quote}` : ref.sources[0].quote}
                  aria-label={`${ref.ref || "Упоминание"} — ${group.title}: открыть в документе`}
                  onClick={() => openSource(docId, ref.sources[0])}>{ref.ref || "упоминание"}</button>
              ))}
            </dd>
          </Fragment>
        ))}
      </dl>
      {texts.length ? (
        <div className="norm-texts">
          <h4>Текст норм в действующей редакции</h4>
          {texts.map((law, i) => <NormText key={`${law.label}-${i}`} law={law} />)}
        </div>
      ) : null}
      {explanations.length ? (
        <div className="norm-texts">
          <h4>Разъяснения Пленума Верховного Суда</h4>
          {explanations.map((law, i) => <NormText key={`${law.label}-${i}`} law={law} />)}
        </div>
      ) : null}
      <p className="review-footnote">Список составлен автоматически по тексту документа. Текст статей и пунктов постановлений Пленума Верховного Суда — из базы Правосвода (статьи — с pravo.gov.ru, постановления — с КонсультантПлюс, действующая редакция); применима ли норма к делу — оценивает юрист. Законы и постановления, которых нет в базе, проверьте отдельно.</p>
    </section>
  );
}

export function SummaryCard({ answer, docId }: { answer: ReviewAnswer; docId: string }) {
  const sections = answer.sections || [];
  const bySection = new Map<string, ReviewPoint[]>();
  for (const point of answer.points) bySection.set(point.section || "", [...(bySection.get(point.section || "") || []), point]);
  const shown = sections.filter(([code]) => bySection.has(code));
  const missing = sections.filter(([code]) => !bySection.has(code)).map(([, name]) => name.toLowerCase());
  const extra = bySection.get("") || [];
  return (
    <div className="review-card">
      {shown.map(([code, name]) => (
        <section key={code} className="review-section">
          <h3>{name}</h3>
          {bySection.get(code)!.map((point, i) => <SummaryItem key={i} point={point} docId={docId} />)}
        </section>
      ))}
      {extra.length ? (
        <section className="review-section">
          <h3>Ещё из документа</h3>
          {extra.map((point, i) => <SummaryItem key={i} point={point} docId={docId} />)}
        </section>
      ) : null}
      {answer.norms && answer.norms.length ? <Norms norms={answer.norms} docId={docId} /> : null}
      {missing.length ? <p className="review-missing">Не нашёл в тексте: {missing.join("; ")}. Если это есть в документе — спросите отдельно.</p> : null}
    </div>
  );
}

// Спорное или слабое место: где в документе, что сказано, почему спорно и что сделать.
function IssuePoint({ point, docId }: { point: ReviewPoint; docId: string }) {
  return (
    <section className="review-point review-issue">
      <div className="review-point-heading"><strong>{point.heading}</strong><span>Вывод ИИ</span></div>
      {point.sources.map((source, i) => <Citation key={i} docId={docId} source={source} />)}
      {point.text ? <p className="review-answer-text">{point.text}</p> : null}
      {point.details && point.details.length ? (
        <dl className="review-details">
          {point.details.map((detail, i) => <Fragment key={i}><dt>{detail.label}</dt><dd>{detail.text}</dd></Fragment>)}
        </dl>
      ) : null}
    </section>
  );
}

export function AnswerPoints({ answer, docId }: { answer: ReviewAnswer; docId: string }) {
  return (
    <>
      {answer.points.map((point, i) => (point.details ? <IssuePoint key={i} point={point} docId={docId} /> : (
        <section key={i} className={`review-point review-${point.kind}`}>
          <div className="review-point-heading"><strong>{point.heading}</strong><span>{LABELS[point.kind]}</span></div>
          <p className="review-answer-text">{point.text}</p>
          {point.kind === "suggestion"
            ? point.sources.map((source, j) => <Citation key={j} docId={docId} source={source} />)
            : <FoldedQuotes docId={docId} sources={point.sources} />}
        </section>
      )))}
    </>
  );
}
